import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Paper,
  useTheme,
} from '@mui/material'
import { motion } from 'framer-motion'

const ExplorePage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [selectedBlackHole, setSelectedBlackHole] = useState(null)

  const blackHoles = [
    {
      id: 'sagittarius-a',
      name: 'Sagittarius A*',
      type: 'Supermassive',
      mass: '4.3 million solar masses',
      distance: '26,000 light-years',
      diameter: '44 million km',
      description: 'The supermassive black hole at the center of our Milky Way galaxy. Recently imaged by the Event Horizon Telescope.',
      image: 'https://images.unsplash.com/photo-1446776877081-d282a0f896e2?w=400&h=300&fit=crop',
      discoveryYear: '1974',
      specialFeatures: ['First imaged black hole', 'Closest supermassive BH', 'Event Horizon Telescope target'],
      simulationParams: { mass: 4.3e6, spin: 0.9, distance: 26000 }
    },
    {
      id: 'm87',
      name: 'M87*',
      type: 'Supermassive',
      mass: '6.5 billion solar masses',
      distance: '53 million light-years',
      diameter: '38 billion km',
      description: 'The first black hole ever directly imaged. Located in the center of galaxy M87, this massive black hole has a mass 6.5 billion times that of our Sun.',
      image: 'https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=400&h=300&fit=crop',
      discoveryYear: '2019',
      specialFeatures: ['First direct image', 'Largest known BH', 'Powerful jet emissions'],
      simulationParams: { mass: 6.5e9, spin: 0.7, distance: 53000000 }
    },
    {
      id: 'cygnus-x1',
      name: 'Cygnus X-1',
      type: 'Stellar Mass',
      mass: '21 solar masses',
      distance: '6,070 light-years',
      diameter: '124 km',
      description: 'One of the first strong candidates for a stellar black hole. It\'s part of a binary system with a blue supergiant star.',
      image: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=400&h=300&fit=crop',
      discoveryYear: '1971',
      specialFeatures: ['First BH candidate', 'Binary system', 'X-ray source'],
      simulationParams: { mass: 21, spin: 0.95, distance: 6070 }
    },
    {
      id: 'ton-618',
      name: 'TON 618',
      type: 'Ultramassive',
      mass: '66 billion solar masses',
      distance: '10.4 billion light-years',
      diameter: '390 billion km',
      description: 'One of the most massive black holes ever discovered. Located in a distant quasar, it challenges our understanding of black hole formation.',
      image: 'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=400&h=300&fit=crop',
      discoveryYear: '2017',
      specialFeatures: ['Ultramassive BH', 'Distant quasar', 'Extreme luminosity'],
      simulationParams: { mass: 6.6e10, spin: 0.8, distance: 10400000000 }
    },
    {
      id: 'gw150914',
      name: 'GW150914',
      type: 'Binary Merger',
      mass: '62 solar masses',
      distance: '1.3 billion light-years',
      diameter: '370 km',
      description: 'The first gravitational wave detection from merging black holes. This historic event confirmed Einstein\'s predictions and opened a new era of astronomy.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
      discoveryYear: '2015',
      specialFeatures: ['First GW detection', 'Binary merger', 'LIGO discovery'],
      simulationParams: { mass: 62, spin: 0.7, distance: 1300000000 }
    },
    {
      id: 'icarus',
      name: 'Icarus',
      type: 'Lensed',
      mass: 'Unknown',
      distance: '9.34 billion light-years',
      diameter: 'Unknown',
      description: 'The most distant individual star ever observed, magnified by gravitational lensing from a foreground galaxy cluster.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      discoveryYear: '2018',
      specialFeatures: ['Most distant star', 'Gravitational lensing', 'Hubble discovery'],
      simulationParams: { mass: 1000, spin: 0.5, distance: 9340000000 }
    }
  ]

  const handleSimulate = (blackHole) => {
    console.log('Simulating black hole:', blackHole)
    console.log('Simulation params:', blackHole.simulationParams)
    setSelectedBlackHole(blackHole)
    
    // Navigate to simulation with URL parameters
    const params = new URLSearchParams({
      mass: blackHole.simulationParams.mass.toString(),
      spin: blackHole.simulationParams.spin.toString(),
      distance: blackHole.simulationParams.distance.toString(),
      name: blackHole.name
    })
    const url = `/?${params.toString()}`
    console.log('Navigating to:', url)
    navigate(url)
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'Supermassive': return 'primary'
      case 'Stellar Mass': return 'secondary'
      case 'Ultramassive': return 'error'
      case 'Binary Merger': return 'warning'
      case 'Lensed': return 'info'
      default: return 'default'
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography
          variant="h2"
          gutterBottom
          align="center"
          sx={{
            fontFamily: 'Orbitron, sans-serif',
            color: 'primary.light',
            textShadow: '0 0 15px rgba(0, 255, 255, 0.7)',
            mb: 2,
          }}
        >
          🌌 Real-World Black Holes
        </Typography>
        
        <Typography
          variant="h5"
          align="center"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            color: 'text.secondary',
            mb: 4,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Explore the most fascinating black holes discovered in our universe. 
          Click "Simulate" to see them in our interactive 3D simulation.
        </Typography>

        <Grid container spacing={3}>
          {blackHoles.map((blackHole, index) => (
            <Grid item xs={12} md={6} lg={4} key={blackHole.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'linear-gradient(145deg, rgba(26, 32, 39, 0.9) 0%, rgba(0, 0, 0, 0.9) 100%)',
                    border: '1px solid rgba(0, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: 'rgba(0, 255, 255, 0.5)',
                      boxShadow: '0 12px 40px rgba(0, 255, 255, 0.2)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={blackHole.image}
                    alt={blackHole.name}
                    sx={{
                      objectFit: 'cover',
                      filter: 'brightness(0.8) contrast(1.2)',
                    }}
                  />
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontFamily: 'Orbitron, sans-serif',
                          color: 'primary.light',
                          fontWeight: 600,
                        }}
                      >
                        {blackHole.name}
                      </Typography>
                      <Chip
                        label={blackHole.type}
                        color={getTypeColor(blackHole.type)}
                        size="small"
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 500,
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        color: 'text.secondary',
                        mb: 2,
                        lineHeight: 1.6,
                      }}
                    >
                      {blackHole.description}
                    </Typography>

                    <Paper
                      sx={{
                        p: 2,
                        mb: 2,
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'Roboto, sans-serif', color: 'text.primary', mb: 1 }}
                      >
                        <strong>Mass:</strong> {blackHole.mass}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'Roboto, sans-serif', color: 'text.primary', mb: 1 }}
                      >
                        <strong>Distance:</strong> {blackHole.distance}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'Roboto, sans-serif', color: 'text.primary', mb: 1 }}
                      >
                        <strong>Diameter:</strong> {blackHole.diameter}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'Roboto, sans-serif', color: 'text.primary' }}
                      >
                        <strong>Discovered:</strong> {blackHole.discoveryYear}
                      </Typography>
                    </Paper>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {blackHole.specialFeatures.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '0.7rem',
                            borderColor: 'rgba(0, 255, 255, 0.3)',
                            color: 'primary.light',
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => handleSimulate(blackHole)}
                      sx={{
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: 600,
                        py: 1.5,
                        background: 'linear-gradient(45deg, #00ffff 30%, #00b3b3 90%)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #00e6e6 30%, #009999 90%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0, 255, 255, 0.4)',
                        },
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      🚀 Simulate This Black Hole
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {selectedBlackHole && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            sx={{ mt: 4 }}
          >
            <Paper
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(0, 0, 0, 0.3) 100%)',
                border: '1px solid rgba(156, 39, 176, 0.3)',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Orbitron, sans-serif',
                  color: 'secondary.light',
                  mb: 1,
                }}
              >
                🎯 Selected: {selectedBlackHole.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  color: 'text.secondary',
                }}
              >
                Navigate to the Simulation page to see this black hole in action!
              </Typography>
            </Paper>
          </motion.div>
        )}
      </motion.div>
    </Container>
  )
}

export default ExplorePage
